import { travelerLastStepPassed } from '@parallel-fluidics/shared/models/travelers';

const HEADER = [
  'type', 'id', 'code', 'part', 'kind', 'status', 'remaining_qty',
  'initial_qty', 'material', 'hop', 'consumed_from', 'shipped',
  'completed', 'order', 'shipped_line_item', 'shipment',
];

const escapeCsv = (value) => {
  let text = value == null ? '' : String(value);
  // Neutralize spreadsheet formula injection: Excel/Sheets evaluate cells
  // starting with = + - @ (even after leading whitespace) — notes and
  // reasons flow into this export.
  if (/^\s*[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Flatten a trace walk into CSV: one row per lot (hop depth and the
 * parent lots it was assembled from) and one per traveler (the lots it
 * consumed, shipped/completed state and order info). Deduped like the
 * tiles — a diamond lot or a multi-lot traveler is one row, with every
 * parent joined into consumed_from.
 *
 * @param {object} trace - traceLotDownstream result.
 * @param {Object<string, string>} partLabelByLotId - From
 *   fetchPartLabelsForLots; lots without an entry fall back to raw IDs.
 * @returns {string} CSV content.
 */
export default function buildLotTraceCsv(trace, partLabelByLotId = {}) {
  // Hop depth: BFS over the child edges from the root.
  const depthByLotId = { [trace.root.id]: 0 };
  let frontier = [trace.root.id];
  while (frontier.length > 0) {
    const next = [];
    frontier.forEach((lotId) => {
      (trace.childLotIdsByLotId[lotId] || []).forEach((childId) => {
        if (depthByLotId[childId] !== undefined) return;
        depthByLotId[childId] = depthByLotId[lotId] + 1;
        next.push(childId);
      });
    });
    frontier = next;
  }

  const parentsByLotId = {};
  Object.entries(trace.childLotIdsByLotId).forEach(([parentId, childIds]) => {
    childIds.forEach((childId) => {
      parentsByLotId[childId] = [...(parentsByLotId[childId] || []), parentId];
    });
  });

  const lotCode = (lotId) => trace.lotsById[lotId]?.code || lotId;

  const lotRows = Object.values(trace.lotsById).map((lot) => [
    'lot',
    lot.id,
    lot.code,
    partLabelByLotId[lot.id] || [lot.projectDocId, lot.pPartDocId].filter(Boolean).join('/'),
    lot.kind,
    lot.status,
    lot.remainingQty,
    lot.initialQty,
    lot.material,
    depthByLotId[lot.id],
    (parentsByLotId[lot.id] || []).map(lotCode).join(', '),
    '', '', '', '', '',
  ]);

  const travelerRows = Object.values(trace.travelersById).map((traveler) => [
    'traveler',
    traveler.id,
    '',
    [traveler.project, traveler.pPart].filter(Boolean).join('-'),
    '', '', '', '', '', '',
    (traveler.componentLotIds || [])
      .filter((componentLotId) => trace.lotsById[componentLotId])
      .map(lotCode)
      .join(', '),
    traveler.shipped ? 'yes' : 'no',
    travelerLastStepPassed(traveler.steps) ? 'yes' : 'no',
    traveler.order,
    traveler.shippedLineItemId,
    traveler.shipmentId,
  ]);

  return [HEADER, ...lotRows, ...travelerRows]
    .map((row) => row.map(escapeCsv).join(','))
    .join('\n');
}
